import React from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Search } from "lucide-react";

/**
 * Reusable Pagination Component with Search
 */
const DataPagination = ({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  onSearch,
  searchValue,
  searchPlaceholder,
  language = "tr",
  showSearch = true,
  className = ""
}) => {
  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    
    if (totalPages <= maxVisible + 2) {
      // Show all pages if total is small
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);
      
      if (currentPage > 3) {
        pages.push('...');
      }
      
      // Show pages around current
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      
      if (currentPage < totalPages - 2) {
        pages.push('...');
      }
      
      // Always show last page
      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  return (
    <div className={`flex flex-col sm:flex-row items-center justify-between gap-4 ${className}`}>
      {/* Search Input */}
      {showSearch && onSearch && (
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            type="text"
            value={searchValue || ""}
            onChange={(e) => onSearch(e.target.value)}
            placeholder={searchPlaceholder || (language === "tr" ? "Ara..." : "Search...")}
            className="pl-9 bg-black/50 border-white/10 rounded-none font-mono text-sm h-9"
          />
        </div>
      )}
      
      {/* Info + Pagination */}
      <div className="flex flex-col sm:flex-row items-center gap-4 w-full sm:w-auto">
        {/* Items Info */}
        <span className="text-xs text-muted-foreground font-mono whitespace-nowrap">
          {totalItems > 0 ? (
            language === "tr" 
              ? `${startItem}-${endItem} / ${totalItems} kayıt`
              : `${startItem}-${endItem} of ${totalItems} items`
          ) : (
            language === "tr" ? "Kayıt yok" : "No items"
          )}
        </span>
        
        {/* Page Numbers */}
        {totalPages > 1 && (
          <div className="flex items-center gap-1">
            {/* First Page */}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onPageChange(1)}
              disabled={currentPage === 1}
              className="h-8 w-8 p-0 font-mono"
            >
              <ChevronsLeft className="w-4 h-4" />
            </Button>
            
            {/* Previous Page */}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="h-8 w-8 p-0 font-mono"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            
            {/* Page Numbers */}
            {getPageNumbers().map((page, idx) => (
              page === '...' ? (
                <span key={`ellipsis-${idx}`} className="px-2 text-muted-foreground font-mono">...</span>
              ) : (
                <Button
                  key={page}
                  size="sm"
                  variant={currentPage === page ? "default" : "ghost"}
                  onClick={() => onPageChange(page)}
                  className={`h-8 w-8 p-0 font-mono text-xs ${currentPage === page ? "bg-primary text-black" : ""}`}
                >
                  {page}
                </Button>
              )
            ))}
            
            {/* Next Page */}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="h-8 w-8 p-0 font-mono"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
            
            {/* Last Page */}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onPageChange(totalPages)}
              disabled={currentPage === totalPages}
              className="h-8 w-8 p-0 font-mono"
            >
              <ChevronsRight className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DataPagination;
